// src/pages/Budgets.jsx
import { Close, Delete, FilterList, Search, Sort } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CssVarsProvider,
  Divider,
  Grid,
  IconButton,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Stack,
  Tooltip,
  Typography,
  useColorScheme,
  useTheme,
} from "@mui/joy";
import { useMediaQuery } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import api from "../api/axios";
import {
  getCategoryDisplay,
  getIconForCategoryName,
} from "../utils/categoryIcons";

export default function Budgets() {
  // data
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // ui state
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [error, setError] = useState("");

  const { mode } = useColorScheme();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const API_URL = process.env.REACT_APP_API_URL;

  // helper: push alert
  const pushAlert = (message, severity = "success", ttl = 3500) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setAlerts((prev) => [...prev, { id, message, severity }]);
    setTimeout(() => {
      setAlerts((prev) => prev.filter((a) => a.id !== id));
    }, ttl);
  };
  const removeAlert = (id) =>
    setAlerts((prev) => prev.filter((a) => a.id !== id));

  // fetch budgets + categories + transactions
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem("token");
    if (!token) {
      setError("No token found. Please log in.");
      setLoading(false);
      return;
    }

    const fetchAll = async () => {
      try {
        setLoading(true);
        setError("");
        const headers = { Authorization: `Bearer ${token}` };

        const [budgetsRes, categoriesRes, transactionsRes] = await Promise.all([
          api?.get
            ? api.get("/budgets", { headers }).then((res) => res.data)
            : fetch(`${API_URL}/budgets`, { headers }).then((r) => r.json()),

          api?.get
            ? api.get("/categories", { headers }).then((res) => res.data)
            : fetch(`${API_URL}/categories`, { headers }).then((r) => r.json()),

          api?.get
            ? api.get("/transactions", { headers }).then((res) => res.data)
            : fetch(`${API_URL}/transactions`, { headers }).then((r) => r.json()),
        ]);

        const budgetsData = budgetsRes?.data ?? budgetsRes;
        const categoriesData = categoriesRes?.data ?? categoriesRes;
        const transactionsData = transactionsRes?.data ?? transactionsRes;

        if (!mounted) return;
        setBudgets(Array.isArray(budgetsData) ? budgetsData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        const msg =
          err?.response?.data?.message || err.message || "Failed to fetch data";
        setError(msg);
        pushAlert(msg, "danger");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      mounted = false;
    };
  }, []);

  // delete handler
  const handleDelete = async (budgetId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      pushAlert("No token present. Login required.", "danger");
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Delete this budget?")) return;

    try {
      if (api.delete) {
        await api.delete(`/budgets/${budgetId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const res = await fetch(`/api/budgets/${budgetId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      }
      setBudgets((prev) =>
        prev.filter((b) => String(b.id) !== String(budgetId)),
      );
      pushAlert("Budget deleted", "success");
    } catch (err) {
      console.error("Delete budget error:", err);
      const m =
        err?.response?.data?.message ||
        err.message ||
        "Failed to delete budget";
      pushAlert(m, "danger");
    }
  };

  // ─── Compute how much was spent for a given budget ───────────────────────
  // Mirrors the spentByCategory logic in Budget.jsx but scoped per-card.
  // monthStr: "YYYY-MM"  |  categoryId: number | string | null
  const computeSpent = React.useCallback(
    (monthStr, categoryId) => {
      if (!monthStr || !Array.isArray(transactions)) return 0;

      // parse "YYYY-MM"
      const parts = String(monthStr).split("-");
      if (parts.length < 2) return 0;
      const targetYear = Number(parts[0]);
      const targetMonth = Number(parts[1]) - 1; // 0-based

      let total = 0;
      for (const tx of transactions) {
        if (!tx || !tx.date) continue;
        const dt = new Date(tx.date);
        if (Number.isNaN(dt.getTime())) continue;
        if (dt.getFullYear() !== targetYear || dt.getMonth() !== targetMonth)
          continue;

        const amount = Number(tx.amount || 0);
        if (amount >= 0) continue; // only expenses (negative amounts)
        const spent = Math.abs(amount);

        // categoryId === null means "all categories" budget
        if (categoryId === null || categoryId === undefined) {
          total += spent;
        } else if (String(tx.category_id) === String(categoryId)) {
          total += spent;
        }
      }
      return total;
    },
    [transactions],
  );
  // ─────────────────────────────────────────────────────────────────────────

  // filtered + sorted list
  const filteredSortedBudgets = React.useMemo(() => {
    let list = budgets || [];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter((b) => {
        const name = String(b.name || b.description || "").toLowerCase();
        const cat = String(
          b.category_name || b.category_id || "",
        ).toLowerCase();
        return (
          name.includes(s) || cat.includes(s) || String(b.id || "").includes(s)
        );
      });
    }
    if (filterCategoryId) {
      list = list.filter(
        (b) => String(b.category_id) === String(filterCategoryId),
      );
    }

    const mapped = list
      .map((b, idx) => {
        const ts = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return { b, idx, ts };
      })
      .sort((a, c) => {
        if (a.ts !== c.ts)
          return sortOrder === "newest" ? c.ts - a.ts : a.ts - c.ts;
        return a.idx - c.idx;
      })
      .map(({ b }) => b);

    return mapped;
  }, [budgets, searchTerm, filterCategoryId, sortOrder]);

  const clearFilters = () => {
    setSearchTerm("");
    setFilterCategoryId("");
    setSortOrder("newest");
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatMonth = (monthStr) => {
    if (!monthStr || typeof monthStr !== "string") return "";
    const [y, m] = monthStr.split("-");
    if (!y || !m) return monthStr;
    const idx = Number(m) - 1;
    try {
      const d = new Date(Number(y), idx, 1);
      return d.toLocaleString(undefined, { month: "short", year: "numeric" });
    } catch {
      return monthStr;
    }
  };

  const resolveCategoryName = (b) => {
    if (!b) return null;
    if (b.category_name) return b.category_name;

    if (
      b.category_id != null &&
      typeof b.category_id === "string" &&
      Number.isNaN(Number(b.category_id))
    ) {
      return b.category_id;
    }

    if (b.category_id != null) {
      const found = categories.find(
        (c) =>
          String(c.id) === String(b.category_id) ||
          String(c.name) === String(b.category_id),
      );
      if (found) return found.name;
    }

    return null;
  };

  const fmt = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

  /* ---------------------------
     Framer Motion variants
     --------------------------- */
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.04,
        when: "beforeChildren",
      },
    },
  };
  const item = {
    hidden: { opacity: 0, y: 12, scale: 0.98 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.28, ease: "easeOut" },
    },
    exit: { opacity: 0, y: -8, scale: 0.98, transition: { duration: 0.18 } },
  };

  return (
    <CssVarsProvider>
      <Grid
        sx={{
          display: "flex",
          justifySelf: isMedium || isSmall ? "flex-end" : "center",
          px: 2,
          py: { xs: 2, sm: 2 },
          bgcolor: "inherit",
          flexDirection: "column",
          gap: 5,
          width: "100%",
          maxWidth: isMedium || isSmall ? "100%" : "70%",
          borderRadius: "2px",
          height: { xs: "105%", sm: "100%" },
        }}
      >
        {/* Header */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography level={isSmall ? "h4" : "h3"} fontFamily="monospace">
              Budgets
            </Typography>

            <Tooltip title="Search / Filter" arrow>
              <IconButton
                variant="outlined"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.28, ease: "easeInOut" }}
                sx={{ overflow: "hidden", mb: 3 }}
              >
                <Stack spacing={2}>
                  <Input
                    startDecorator={<Search />}
                    placeholder="Search budgets, category or id..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size={isSmall ? "sm" : "md"}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Select
                      placeholder="Filter category"
                      value={filterCategoryId ?? ""}
                      onChange={(e, value) => setFilterCategoryId(value || "")}
                      size={isSmall ? "sm" : "md"}
                      sx={{ flex: 1 }}
                    >
                      <Option value="">All Categories</Option>
                      {categories.map((c) => (
                        <Option key={c.id} value={c.id}>
                          {c.name}
                        </Option>
                      ))}
                    </Select>

                    <ButtonGroup
                      variant="outlined"
                      size="sm"
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <Button
                        startDecorator={<Sort />}
                        variant={sortOrder === "newest" ? "solid" : "plain"}
                        onClick={() => setSortOrder("newest")}
                        sx={{ flex: { xs: 1, sm: "none" } }}
                      >
                        Newest
                      </Button>
                      <Button
                        endDecorator={
                          <Sort style={{ transform: "rotate(180deg)" }} />
                        }
                        variant={sortOrder === "oldest" ? "solid" : "plain"}
                        onClick={() => setSortOrder("oldest")}
                        sx={{ flex: { xs: 1, sm: "none" } }}
                      >
                        Oldest
                      </Button>
                    </ButtonGroup>
                  </Stack>

                  <Button
                    variant="plain"
                    size="sm"
                    onClick={clearFilters}
                    fullWidth={isSmall}
                  >
                    Clear All Filters
                  </Button>
                </Stack>
              </Box>
            )}
          </AnimatePresence>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Count */}
        <Typography level="body-sm" sx={{ mb: 1 }} fontFamily="monospace">
          Showing {filteredSortedBudgets.length} budget
          {filteredSortedBudgets.length !== 1 ? "s" : ""}
        </Typography>

        {/* Card Grid */}
        <Box
          component={motion.div}
          variants={container}
          initial="hidden"
          animate="show"
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "repeat(1, minmax(0, 1fr))",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(2, 1fr))",
            },
          }}
        >
          <AnimatePresence>
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                <Sheet variant="soft" sx={{ p: 3, textAlign: "center" }}>
                  <Typography>Loading budgets...</Typography>
                </Sheet>
              </motion.div>
            ) : filteredSortedBudgets.length > 0 ? (
              filteredSortedBudgets.map((b) => {
                const categoryName = resolveCategoryName(b);
                const categoryString = b.name;
                const { label, icon } = getCategoryDisplay(categoryString);

                const amount = Number(b.amount || 0);
                const monthLabel = formatMonth(b.month) || b.month || "";
                const title =
                  b.name ||
                  categoryName ||
                  (monthLabel ? `${monthLabel} Budget` : "Unnamed Budget");

                // ── Spent / remaining for THIS card ──────────────────────
                const spent = computeSpent(b.month, b.category_id ?? null);
                const remaining = amount - spent;
                const pct = amount > 0 ? Math.min((spent / amount) * 100, 100) : 0;
                const isOverBudget = spent > amount && amount > 0;
                const progressColor = isOverBudget
                  ? "danger"
                  : pct >= 80
                  ? "warning"
                  : "success";
                // ─────────────────────────────────────────────────────────

                return (
                  <motion.div
                    key={b.id}
                    variants={item}
                    initial="hidden"
                    animate="show"
                    exit="exit"
                    layout
                  >
                    <Sheet
                      variant={mode === "light" ? "outlined" : "soft"}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        minHeight: 160,
                        position: "relative",
                        overflow: "hidden",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "default",
                        border:
                          mode === "light"
                            ? "1px solid rgba(0, 0, 0, 0.08)"
                            : "1px solid rgba(255, 255, 255, 0.08)",
                        background:
                          mode === "light"
                            ? "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)"
                            : "linear-gradient(135deg, rgba(0, 0, 0, 1) 0%, rgba(4, 7, 8, 0) 100%)",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow:
                            mode === "light"
                              ? "0 12px 24px rgba(0, 0, 0, 0.08)"
                              : "0 12px 24px rgba(0, 0, 0, 0.4)",
                          border:
                            mode === "light"
                              ? "1px solid rgba(0, 0, 0, 0.12)"
                              : "1px solid rgba(255, 255, 255, 0.12)",
                        },
                      }}
                    >
                      {/* Decorative gradient accent */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          background:
                            "linear-gradient(90deg, #ffffff, rgba(15, 23, 42, 0.6) 0%, #f8f9fa)",
                          opacity: 0.8,
                        }}
                      />

                      {/* Header with title and delete */}
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            fontFamily="monospace"
                            level="title-md"
                            fontWeight={600}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                              mb: 0.5,
                            }}
                          >
                            {title} Budget
                          </Typography>
                          <Typography
                            level="body-xs"
                            fontFamily="monospace"
                            sx={{
                              color: "text.tertiary",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {monthLabel
                              ? monthLabel
                              : formatTimestamp(b.createdAt)}
                            {b.recurring ? " • Recurring" : ""}
                          </Typography>
                        </Box>

                        <IconButton
                          variant="plain"
                          color="danger"
                          size="sm"
                          onClick={() => handleDelete(b.id)}
                          aria-label="delete budget"
                          sx={{
                            transition: "all 0.2s",
                            "&:hover": {
                              transform: "scale(1.1)",
                              backgroundColor: "rgba(239, 68, 68, 0.08)",
                            },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>

                      {/* Amount - Large and prominent */}
                      <Box
                        sx={{
                          my: 1,
                          display: "flex",
                          alignItems: "baseline",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          level="h2"
                          fontFamily="monospace"
                          fontWeight={700}
                          sx={{
                            background:
                              mode === "light"
                                ? "linear-gradient(135deg, #1e293b 0%, #475569 100%)"
                                : "linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {fmt.format(amount)}
                        </Typography>
                      </Box>

                      {/* ── Spent / Remaining progress section ── */}
                      <Box sx={{ mt: -1, mb: 0.5 }}>
                        {/* Progress bar */}
                        <LinearProgress
                          determinate
                          value={pct}
                          color={progressColor}
                          size="sm"
                          sx={{ borderRadius: 99, mb: 1 }}
                        />

                        {/* Spent + Remaining labels */}
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Tooltip
                            title={`${pct.toFixed(1)}% of budget used`}
                            arrow
                            size="sm"
                          >
                            <Typography
                              level="body-xs"
                              fontFamily="monospace"
                              sx={{
                                color: isOverBudget
                                  ? "danger.500"
                                  : "text.secondary",
                                fontWeight: 600,
                              }}
                            >
                              {fmt.format(spent)} spent
                            </Typography>
                          </Tooltip>

                          <Typography
                            level="body-xs"
                            fontFamily="monospace"
                            sx={{
                              color: isOverBudget
                                ? "danger.400"
                                : remaining === 0
                                ? "warning.400"
                                : "text.tertiary",
                            }}
                          >
                            {isOverBudget
                              ? `${fmt.format(Math.abs(remaining))} over`
                              : `${fmt.format(remaining)} left`}
                          </Typography>
                        </Stack>
                      </Box>
                      {/* ──────────────────────────────────────── */}

                      {/* Category and notes section */}
                      <Stack spacing={1} sx={{ mt: "auto" }}>
                        <Chip
                          size="sm"
                          variant="soft"
                          startDecorator={getIconForCategoryName(
                            categoryString,
                            mode === "light" ? "#454545 " : undefined,
                          )}
                          sx={{
                            alignSelf: "flex-start",
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.5,
                            background:
                              mode === "light"
                                ? "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1))"
                                : "linear-gradient(135deg, rgba(59, 130, 246, 0.18), rgba(139, 92, 246, 0.18))",
                            border:
                              mode === "light"
                                ? "1px solid rgba(59, 130, 246, 0.15)"
                                : "1px solid rgba(59, 130, 246, 0.25)",
                          }}
                        >
                          <Typography
                            fontFamily="monospace"
                            level="body-xs"
                            fontWeight={600}
                            sx={{
                              background:
                                "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            {label}
                          </Typography>
                        </Chip>

                        {b.notes && (
                          <Typography
                            level="body-xs"
                            fontFamily="monospace"
                            sx={{
                              color: "text.secondary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              lineHeight: 1.5,
                              minHeight: "2.4em",
                            }}
                          >
                            {b.notes}
                          </Typography>
                        )}
                      </Stack>
                    </Sheet>
                  </motion.div>
                );
              })
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Typography
                  fontFamily="monospace"
                  color="text.secondary"
                  level="body-sm"
                  display={"flex"}
                  justifyContent={{ xs: "center", sm: "flex-end" }}
                >
                  No budgets found
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* Alerts (bottom-left) */}
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 24 },
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minWidth: { xs: "calc(100% - 32px)", sm: 280 },
            maxWidth: { xs: "calc(100% - 32px)", sm: 400 },
          }}
        >
          <AnimatePresence>
            {alerts.map((a) => (
              <Alert
                component={motion.div}
                key={a.id}
                initial={{ opacity: 0, x: -100, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -100, scale: 0.9 }}
                transition={{ duration: 0.25 }}
                variant={mode === "light" ? "soft" : "solid"}
                color={a.severity === "danger" ? "danger" : a.severity}
                size="sm"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography fontFamily="monospace">{a.message}</Typography>
                </Box>
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => removeAlert(a.id)}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Alert>
            ))}
          </AnimatePresence>
        </Box>
      </Grid>
    </CssVarsProvider>
  );
}